import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Save, Image as ImageIcon, ChevronDown, Calendar as CalendarIcon, Check, Plus, MapPin } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_LOCATION = "Albanian Aligners Academy";
const DEFAULT_LOCATION_URL = "https://maps.app.goo.gl/wQK5YLKZjgvJJ27J8";

const uploadFile = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const res = await axios.post(`${API}/upload`, formData, { headers: { "Content-Type": "multipart/form-data" } });
  return res.data.url;
};

// ---------- Date helpers (DD/MM/YYYY <-> Date) ----------
const parseDDMMYYYY = (str) => {
  if (!str) return undefined;
  const parts = String(str).split("/");
  if (parts.length !== 3) return undefined;
  // Use only the last day if it's a range like "10-11"
  const dayPart = parts[0].split("-").pop();
  const day = parseInt(dayPart, 10);
  const month = parseInt(parts[1], 10);
  const year = parseInt(parts[2], 10);
  if (!day || !month || !year) return undefined;
  const d = new Date(year, month - 1, day);
  return isNaN(d.getTime()) ? undefined : d;
};
const formatDDMMYYYY = (d) => {
  if (!d) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
};

// ---------- DatePicker (Calendar + free text for ranges) ----------
const DatePickerField = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const selectedDate = useMemo(() => parseDDMMYYYY(value), [value]);
  return (
    <div className="flex items-center gap-2">
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="15/12/2026 ose 10-11/12/2026"
        className="rounded-lg border-slate-200 bg-slate-50 flex-1"
        data-testid="event-date"
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="h-10 w-10 rounded-lg border border-slate-200 bg-white flex items-center justify-center text-[#08707A] hover:bg-slate-50 transition-colors shrink-0"
            aria-label="Hap kalendarin"
            data-testid="event-date-picker-btn"
          >
            <CalendarIcon size={16} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 z-[60]" align="end">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(d) => { if (d) { onChange(formatDDMMYYYY(d)); setOpen(false); } }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

// ---------- TrainerCombobox (search + auto-create) ----------
const TrainerCombobox = ({ value, onChange, trainers, onTrainerCreated }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const containerRef = useRef(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return trainers;
    return trainers.filter((t) => (t.name || "").toLowerCase().includes(q));
  }, [trainers, query]);

  const exactMatch = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q && trainers.some((t) => (t.name || "").toLowerCase() === q);
  }, [trainers, query]);

  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const selectTrainer = (t) => {
    onChange(t.name);
    setQuery("");
    setOpen(false);
  };

  const createTrainer = async () => {
    const name = query.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/trainers`, {
        name,
        specialty: "",
        bio: "",
        short_bio: "",
        image_url: "",
        order: 0,
      });
      const created = res.data;
      onTrainerCreated(created);
      onChange(created.name);
      toast.success(`Trajneri "${created.name}" u krijua.`);
      setQuery("");
      setOpen(false);
    } catch (err) {
      console.error(err);
      toast.error("Krijimi i trajnerit deshtoi.");
    }
    setCreating(false);
  };

  return (
    <div ref={containerRef} className="relative" data-testid="trainer-combobox">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-left text-sm hover:bg-slate-100 transition-colors"
        data-testid="trainer-combobox-trigger"
      >
        <span className={value ? "text-[#0A0D10]" : "text-slate-400"}>
          {value || "Zgjidh ose shkruaj nje trajner..."}
        </span>
        <ChevronDown size={16} className="text-slate-400" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1.5 w-full bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden" data-testid="trainer-combobox-list">
          <div className="p-2 border-b border-slate-100">
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Kerko trajner..."
              className="rounded-md border-slate-200 text-sm h-9"
              data-testid="trainer-combobox-search"
            />
          </div>
          <div className="max-h-60 overflow-y-auto">
            {filtered.length === 0 && !query.trim() && (
              <p className="px-3 py-3 text-xs text-slate-400">Asnje trajner ne sistem.</p>
            )}
            {filtered.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => selectTrainer(t)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-slate-50 transition-colors"
                data-testid={`trainer-option-${t.id}`}
              >
                <Check size={14} className={value === t.name ? "text-[#08707A]" : "text-transparent"} />
                <span className="text-[#0A0D10]">{t.name}</span>
                {t.specialty && <span className="text-xs text-slate-400 ml-auto">{t.specialty}</span>}
              </button>
            ))}
            {query.trim() && !exactMatch && (
              <button
                type="button"
                onClick={createTrainer}
                disabled={creating}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left bg-[#08707A]/5 hover:bg-[#08707A]/10 border-t border-slate-100 text-[#08707A] font-semibold disabled:opacity-50"
                data-testid="trainer-combobox-create"
              >
                <Plus size={14} />
                {creating ? "Duke krijuar..." : `Krijo trajner: "${query.trim()}"`}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const AdminEventForm = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const isEditing = !!eventId;

  const [form, setForm] = useState({
    title: "", description: "", speaker: "", date: "", price: "",
    location: DEFAULT_LOCATION, location_url: DEFAULT_LOCATION_URL,
    schedule: "", image_url: "", what_you_learn: "", who_for: "", agenda: "", category: "auto",
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryVideos, setGalleryVideos] = useState([]);
  const [days, setDays] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [tiers, setTiers] = useState([]); // [{label, price, start_date, end_date}]

  useEffect(() => {
    axios.get(`${API}/trainers`).then((r) => setTrainers(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (isEditing) {
      axios.get(`${API}/events/${eventId}`).then((r) => {
        const e = r.data;
        setForm({
          title: e.title || "", description: e.description || "", speaker: e.speaker || "",
          date: e.date || "", price: e.price || "",
          location: e.location ?? DEFAULT_LOCATION,
          location_url: e.location_url ?? (e.location ? "" : DEFAULT_LOCATION_URL),
          schedule: e.schedule || "", image_url: e.image_url || "",
          what_you_learn: e.what_you_learn || "", who_for: e.who_for || "",
          agenda: e.agenda || "", category: e.category || "auto",
        });
        setGalleryImages(e.gallery_images || []);
        const vids = (e.gallery_videos || []).map((v) => (typeof v === "string" ? { url: v, poster: "" } : v));
        setGalleryVideos(vids);
        // Normalise legacy topic strings -> {title, description}
        const normDays = (e.days || []).map((d) => ({
          title: d.title || "",
          topics: (d.topics || []).map((t) =>
            typeof t === "string" ? { title: t, description: "" } : { title: t.title || "", description: t.description || "" }
          ),
        }));
        setDays(normDays);
        setTiers((e.pricing_tiers || []).map((t) => ({
          label: t.label || "",
          price: t.price || "",
          start_date: t.start_date || "",
          end_date: t.end_date || "",
          features: Array.isArray(t.features) ? t.features.join("\n") : (t.features || ""),
        })));
      }).catch(() => navigate("/paneli/events"));
    }
  }, [eventId, isEditing, navigate]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setForm((f) => ({ ...f, image_url: url }));
    } catch (err) { console.error(err); }
    setUploading(false);
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadFile(f));
      setGalleryImages((g) => [...g, ...urls]);
    } catch (err) { console.error(err); }
    setUploading(false);
  };

  const handleVideoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const newVids = [];
      for (const f of files) newVids.push({ url: await uploadFile(f), poster: "" });
      setGalleryVideos((g) => [...g, ...newVids]);
    } catch (err) { console.error(err); }
    setUploading(false);
  };

  const handleVideoPosterUpload = async (idx, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setGalleryVideos((vids) => {
        const next = [...vids];
        next[idx] = { ...next[idx], poster: url };
        return next;
      });
    } catch (err) { console.error(err); }
    setUploading(false);
  };

  const removeVideo = (idx) => setGalleryVideos((v) => v.filter((_, i) => i !== idx));

  // Days handlers (topics are objects: {title, description})
  const setDayCount = (n) => {
    const count = Math.max(0, Math.min(10, Number(n) || 0));
    const next = [...days];
    while (next.length < count) next.push({ title: `DITA ${next.length + 1}`, topics: [{ title: "", description: "" }] });
    while (next.length > count) next.pop();
    setDays(next);
  };
  const updateDayTitle = (idx, title) => {
    const next = [...days]; next[idx] = { ...next[idx], title }; setDays(next);
  };
  const updateDayTopicField = (idx, tIdx, field, val) => {
    const next = [...days];
    const topics = [...(next[idx].topics || [])];
    const current = typeof topics[tIdx] === "string" ? { title: topics[tIdx], description: "" } : (topics[tIdx] || { title: "", description: "" });
    topics[tIdx] = { ...current, [field]: val };
    next[idx] = { ...next[idx], topics };
    setDays(next);
  };
  const addDayTopic = (idx) => {
    const next = [...days];
    next[idx] = { ...next[idx], topics: [...(next[idx].topics || []), { title: "", description: "" }] };
    setDays(next);
  };
  const removeDayTopic = (idx, tIdx) => {
    const next = [...days];
    next[idx] = { ...next[idx], topics: next[idx].topics.filter((_, i) => i !== tIdx) };
    setDays(next);
  };

  // Pricing tier handlers
  const addTier = () => setTiers((arr) => [...arr, { label: "", price: "", start_date: "", end_date: "", features: "" }]);
  const updateTier = (idx, field, val) => {
    setTiers((arr) => {
      const next = [...arr];
      next[idx] = { ...next[idx], [field]: val };
      return next;
    });
  };
  const removeTier = (idx) => setTiers((arr) => arr.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        gallery_images: galleryImages,
        gallery_videos: galleryVideos,
        days: days
          .map((d) => ({
            title: (d.title || "").trim(),
            topics: (d.topics || [])
              .map((t) => {
                if (typeof t === "string") return { title: t.trim(), description: "" };
                return { title: (t.title || "").trim(), description: (t.description || "").trim() };
              })
              .filter((t) => t.title || t.description),
          }))
          .filter((d) => d.title || d.topics.length),
        pricing_tiers: tiers
          .map((t) => ({
            label: (t.label || "").trim(),
            price: (t.price || "").toString().trim(),
            start_date: (t.start_date || "").trim(),
            end_date: (t.end_date || "").trim(),
            features: (t.features || "")
              .split("\n")
              .map((s) => s.trim())
              .filter(Boolean),
          }))
          .filter((t) => t.label || t.price),
      };
      let res;
      if (isEditing) {
        res = await axios.put(`${API}/events/${eventId}`, payload);
      } else {
        res = await axios.post(`${API}/events`, payload);
      }
      toast.success("U ruajt me sukses");
      // Stay on the form. If it was a new event, switch to edit mode for that event.
      if (!isEditing && res?.data?.id) {
        navigate(`/paneli/events/edit/${res.data.id}`, { replace: true });
      }
    } catch (err) {
      console.error(err);
      toast.error("Ruajtja deshtoi. Provoni perseri.");
    }
    setSaving(false);
  };

  const imgSrc = (url) => (url && url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);

  return (
    <div data-testid="admin-event-form">
      <div className="flex items-center gap-4 mb-8">
        <button onClick={() => navigate("/paneli/events")} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-testid="back-to-events">
          <ArrowLeft size={20} className="text-[#4A5568]" />
        </button>
        <div>
          <h1 className="text-2xl font-bold font-['Inter_Tight']">{isEditing ? "Edito Eventin" : "Shto Event te Ri"}</h1>
          <p className="text-sm text-[#828D9E] mt-0.5">{isEditing ? "Ndrysho te dhenat e eventit" : "Ploteso fushat per te shtuar nje event te ri"}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Info */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-base font-bold text-[#0A0D10] mb-5 pb-3 border-b border-slate-100">Informacion Baze</h2>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-semibold text-[#4A5568] mb-1.5 block">Titulli i Eventit *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="psh. Modern Implantology Masterclass" className="rounded-lg border-slate-200 bg-slate-50" data-testid="event-title" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-[#4A5568] mb-1.5 block">Pershkrimi *</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={5} placeholder="Pershkrimi i detajuar i eventit..." className="rounded-lg border-slate-200 bg-slate-50" data-testid="event-description" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold text-[#4A5568] mb-1.5 block">Folesi / Trajneri *</Label>
                <TrainerCombobox
                  value={form.speaker}
                  onChange={(v) => setForm((f) => ({ ...f, speaker: v }))}
                  trainers={trainers}
                  onTrainerCreated={(t) => setTrainers((arr) => [...arr, t])}
                />
              </div>
              <div>
                <Label className="text-sm font-semibold text-[#4A5568] mb-1.5 block">Vendndodhja</Label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="psh. Albanian Aligners Academy" className="rounded-lg border-slate-200 bg-slate-50" data-testid="event-location" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-semibold text-[#4A5568] mb-1.5 block flex items-center gap-1.5"><MapPin size={13} /> Link Google Maps (opsional)</Label>
              <Input value={form.location_url} onChange={(e) => setForm({ ...form, location_url: e.target.value })} placeholder="https://maps.app.goo.gl/..." className="rounded-lg border-slate-200 bg-slate-50" data-testid="event-location-url" />
              <p className="text-xs text-[#828D9E] mt-1">Nese vendoset, emri i vendit ne faqen e eventit do te jete i klikueshem.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm font-semibold text-[#4A5568] mb-1.5 block">Data *</Label>
                <DatePickerField value={form.date} onChange={(v) => setForm((f) => ({ ...f, date: v }))} />
              </div>
              <div>
                <Label className="text-sm font-semibold text-[#4A5568] mb-1.5 block">Orari</Label>
                <Input value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })} placeholder="09:00 - 17:00" className="rounded-lg border-slate-200 bg-slate-50" data-testid="event-schedule" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-[#4A5568] mb-1.5 block">Cmimi Bazë (EUR)</Label>
                <Input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="200.00" className="rounded-lg border-slate-200 bg-slate-50" data-testid="event-price" />
                <p className="text-xs text-[#828D9E] mt-1">Përdoret nëse nuk ka faza me cmime.</p>
              </div>
            </div>
            <div>
              <Label className="text-sm font-semibold text-[#4A5568] mb-1.5 block">Kategoria</Label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm" data-testid="event-category">
                <option value="auto">Automatike (bazuar ne date)</option>
                <option value="upcoming">E ardhshme</option>
                <option value="past">E kaluar</option>
              </select>
            </div>
          </div>
        </div>

        {/* Pricing Tiers */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="pricing-tiers-section">
          <h2 className="text-base font-bold text-[#0A0D10] mb-2 pb-3 border-b border-slate-100">Cmime me Faza (opsionale)</h2>
          <p className="text-xs text-[#828D9E] mb-4">
            Shto disa faza me cmime të ndryshme sipas datës (psh. Early Bird, Standard, Premium). Cmimi aktiv përcaktohet automatikisht nga data e sotme. Nëse nuk plotësoni asnjë fazë, përdoret <strong>Cmimi Bazë</strong>.
          </p>
          {tiers.length > 0 && (
            <div className="space-y-3 mb-4">
              {tiers.map((t, idx) => (
                <div key={idx} className="border border-slate-200 rounded-xl p-4 bg-slate-50" data-testid={`tier-row-${idx}`}>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                    <div className="md:col-span-4">
                      <Label className="text-xs font-semibold text-[#4A5568] mb-1 block">Etiketa</Label>
                      <Input value={t.label} onChange={(e) => updateTier(idx, "label", e.target.value)} placeholder="psh. Early Bird" className="rounded-md border-slate-200 bg-white" data-testid={`tier-label-${idx}`} />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs font-semibold text-[#4A5568] mb-1 block">Cmimi (EUR)</Label>
                      <Input value={t.price} onChange={(e) => updateTier(idx, "price", e.target.value)} placeholder="300" className="rounded-md border-slate-200 bg-white" data-testid={`tier-price-${idx}`} />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs font-semibold text-[#4A5568] mb-1 block">Nga (data)</Label>
                      <Input value={t.start_date} onChange={(e) => updateTier(idx, "start_date", e.target.value)} placeholder="01/05/2026" className="rounded-md border-slate-200 bg-white" data-testid={`tier-start-${idx}`} />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs font-semibold text-[#4A5568] mb-1 block">Deri (data)</Label>
                      <Input value={t.end_date} onChange={(e) => updateTier(idx, "end_date", e.target.value)} placeholder="31/07/2026" className="rounded-md border-slate-200 bg-white" data-testid={`tier-end-${idx}`} />
                    </div>
                    <div className="md:col-span-2 flex justify-end md:justify-start">
                      <button type="button" onClick={() => removeTier(idx)} className="rounded-md px-3 py-2 text-xs font-semibold text-red-500 hover:bg-red-50 transition-colors" data-testid={`tier-remove-${idx}`}>
                        Fshij
                      </button>
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label className="text-xs font-semibold text-[#4A5568] mb-1 block">Përfitimet (një për rresht)</Label>
                    <Textarea
                      value={t.features || ""}
                      onChange={(e) => updateTier(idx, "features", e.target.value)}
                      rows={3}
                      placeholder="Akses në event&#10;Certificate of Attendance&#10;Early access pricing"
                      className="rounded-md border-slate-200 bg-white text-sm"
                      data-testid={`tier-features-${idx}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          <button type="button" onClick={addTier} className="rounded-lg px-4 py-2.5 text-sm font-semibold border border-dashed border-[#08707A] text-[#08707A] hover:bg-[#08707A]/5 transition-colors inline-flex items-center gap-2" data-testid="tier-add-btn">
            <Plus size={14} /> Shto fazë me çmim
          </button>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-base font-bold text-[#0A0D10] mb-5 pb-3 border-b border-slate-100">Foto Kryesore</h2>
          <div className="flex items-start gap-6">
            {form.image_url ? (
              <div className="relative">
                <img src={imgSrc(form.image_url)} alt="Cover" className="w-48 h-32 rounded-lg object-cover border border-slate-200" />
                <button type="button" onClick={() => setForm({ ...form, image_url: "" })} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs flex items-center justify-center shadow-sm hover:bg-red-600" data-testid="remove-cover-img">x</button>
              </div>
            ) : (
              <div className="w-48 h-32 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400">
                <ImageIcon size={32} />
              </div>
            )}
            <div className="flex-1">
              <input type="file" accept="image/*" onChange={handleImageUpload} className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#08707A]/10 file:text-[#08707A] hover:file:bg-[#08707A]/20" data-testid="event-cover-upload" />
              {uploading && <p className="text-xs text-[#1CA3B0] mt-2">Duke ngarkuar...</p>}
              <p className="text-xs text-[#828D9E] mt-2">Rekomandohet: 1200x600px, max 5MB</p>
            </div>
          </div>
        </div>

        {/* Content Details */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-base font-bold text-[#0A0D10] mb-5 pb-3 border-b border-slate-100">Permbajtja e Detajuar</h2>
          <div className="space-y-5">
            <div>
              <Label className="text-sm font-semibold text-[#4A5568] mb-1.5 block">Cfare do te mesohet</Label>
              <Textarea value={form.what_you_learn} onChange={(e) => setForm({ ...form, what_you_learn: e.target.value })} rows={5} placeholder="Shkruaj secilen pike ne nje rresht te ri..." className="rounded-lg border-slate-200 bg-slate-50" data-testid="event-learn" />
              <p className="text-xs text-[#828D9E] mt-1">Shkruaj cdo pike ne nje rresht te ri</p>
            </div>
            <div>
              <Label className="text-sm font-semibold text-[#4A5568] mb-1.5 block">Kujt i dedikohet</Label>
              <Textarea value={form.who_for} onChange={(e) => setForm({ ...form, who_for: e.target.value })} rows={3} placeholder="psh. Dentiste, Ortodonte, Studentet e stomatologjise..." className="rounded-lg border-slate-200 bg-slate-50" data-testid="event-who" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-[#4A5568] mb-1.5 block">Axhenda / Programi</Label>
              <Textarea value={form.agenda} onChange={(e) => setForm({ ...form, agenda: e.target.value })} rows={6} placeholder="09:00 - 10:00 Prezantim&#10;10:00 - 12:00 Sesioni i pare&#10;..." className="rounded-lg border-slate-200 bg-slate-50" data-testid="event-agenda" />
              <p className="text-xs text-[#828D9E] mt-1">Shkruaj cdo pike te programit ne nje rresht te ri</p>
            </div>
          </div>
        </div>

        {/* Gallery Images */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-base font-bold text-[#0A0D10] mb-5 pb-3 border-b border-slate-100">Galeria e Eventit</h2>
          <input type="file" accept="image/*" multiple onChange={handleGalleryUpload} className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#08707A]/10 file:text-[#08707A] hover:file:bg-[#08707A]/20 mb-4" data-testid="event-gallery-upload" />
          {uploading && <p className="text-xs text-[#1CA3B0] mb-3">Duke ngarkuar imazhet...</p>}
          {galleryImages.length > 0 && (
            <div className="flex flex-wrap gap-3">
              {galleryImages.map((url, idx) => (
                <div key={idx} className="relative group">
                  <img src={imgSrc(url)} alt="" className="w-24 h-24 rounded-lg object-cover border border-slate-200" />
                  <button type="button" onClick={() => setGalleryImages(galleryImages.filter((_, j) => j !== idx))} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full text-[10px] flex items-center justify-center shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">x</button>
                </div>
              ))}
            </div>
          )}
          {galleryImages.length === 0 && <p className="text-sm text-[#828D9E]">Asnje imazh ne galeri. Ngarko imazhe sipas deshires.</p>}
        </div>

        {/* Gallery Videos */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-base font-bold text-[#0A0D10] mb-2 pb-3 border-b border-slate-100">Video Galeri (opsionale)</h2>
          <p className="text-xs text-[#828D9E] mb-4">Ngarko video nga eventi. Per cdo video mund te shtosh nje foto placeholder (poster) qe shfaqet para se te luaje.</p>
          <input type="file" accept="video/*" multiple onChange={handleVideoUpload} className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#08707A]/10 file:text-[#08707A] hover:file:bg-[#08707A]/20 mb-4" data-testid="event-video-upload" />
          {uploading && <p className="text-xs text-[#1CA3B0] mb-3">Duke ngarkuar...</p>}
          {galleryVideos.length > 0 ? (
            <div className="space-y-4">
              {galleryVideos.map((v, idx) => (
                <div key={idx} className="border border-slate-200 rounded-lg p-4 flex items-start gap-4 bg-slate-50">
                  <div className="w-40 shrink-0">
                    {v.poster ? (
                      <img src={imgSrc(v.poster)} alt="poster" className="w-40 h-24 rounded-md object-cover border border-slate-200" />
                    ) : (
                      <div className="w-40 h-24 rounded-md bg-slate-200 flex items-center justify-center text-slate-400 text-xs text-center px-2">Pa poster</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-[#4A5568] truncate mb-2 font-mono">{v.url}</p>
                    <label className="text-xs font-semibold text-[#4A5568] block mb-1">Poster placeholder</label>
                    <input type="file" accept="image/*" onChange={(e) => handleVideoPosterUpload(idx, e)} className="text-xs file:mr-2 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-[#08707A]/10 file:text-[#08707A]" data-testid={`video-poster-${idx}`} />
                  </div>
                  <button type="button" onClick={() => removeVideo(idx)} className="text-red-500 hover:text-red-700 text-sm font-semibold shrink-0" data-testid={`remove-video-${idx}`}>
                    Fshij
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#828D9E]">Asnje video. Klikoni butonin lart per te ngarkuar video.</p>
          )}
        </div>

        {/* Day-by-day Tematikat */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-base font-bold text-[#0A0D10] mb-2 pb-3 border-b border-slate-100">Programi Ditor (Tematikat)</h2>
          <p className="text-xs text-[#828D9E] mb-4">Caktoni sa dite zgjat eventi dhe per cdo dite, listoni temat qe trajtohen. Kjo seksion shfaqet ne faqen e detajeve te eventit.</p>
          <div className="flex items-center gap-3 mb-5">
            <Label className="text-sm font-semibold text-[#4A5568]">Numri i diteve:</Label>
            <input type="number" min={0} max={10} value={days.length} onChange={(e) => setDayCount(e.target.value)} className="w-20 border border-slate-200 rounded-lg px-3 py-2 text-sm bg-slate-50 focus:ring-2 focus:ring-[#3CD4E1]/30 outline-none" data-testid="event-day-count" />
            {days.length > 0 && <span className="text-xs text-[#828D9E]">({days.length} dite)</span>}
          </div>
          {days.length > 0 && (
            <div className="space-y-5">
              {days.map((d, idx) => (
                <div key={idx} className="border border-slate-200 rounded-xl p-4 bg-slate-50" data-testid={`day-editor-${idx}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0" style={{ background: "linear-gradient(135deg, #3CD4E1, #1CA3B0)" }}>{idx + 1}</span>
                    <Input value={d.title} onChange={(e) => updateDayTitle(idx, e.target.value)} placeholder={`DITA ${idx + 1}`} className="flex-1 rounded-lg border-slate-200 bg-white font-semibold" data-testid={`day-title-${idx}`} />
                  </div>
                  <div className="space-y-3 pl-11">
                    {(d.topics || []).map((tp, ti) => {
                      const item = typeof tp === "string" ? { title: tp, description: "" } : (tp || { title: "", description: "" });
                      return (
                      <div key={ti} className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`day-topic-${idx}-${ti}`}>
                        <div className="flex items-start gap-2">
                          <span className="mt-2 text-[11px] font-bold text-[#08707A] shrink-0 w-6">{ti + 1})</span>
                          <div className="flex-1 space-y-2">
                            <Input
                              value={item.title}
                              onChange={(e) => updateDayTopicField(idx, ti, "title", e.target.value)}
                              placeholder={`Titulli i temes ${ti + 1}`}
                              className="rounded-md border-slate-200 bg-slate-50 font-semibold"
                              data-testid={`day-topic-title-${idx}-${ti}`}
                            />
                            <Textarea
                              value={item.description}
                              onChange={(e) => updateDayTopicField(idx, ti, "description", e.target.value)}
                              placeholder="Pershkrimi i temes (opsional). Cdo rresht do te shfaqet si pike."
                              rows={3}
                              className="rounded-md border-slate-200 bg-slate-50 text-sm"
                              data-testid={`day-topic-desc-${idx}-${ti}`}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removeDayTopic(idx, ti)}
                            className="text-red-400 hover:text-red-600 text-base w-7 h-7 rounded hover:bg-red-50 shrink-0 flex items-center justify-center"
                            aria-label="Fshij temen"
                            data-testid={`day-topic-remove-${idx}-${ti}`}
                          >×</button>
                        </div>
                      </div>
                      );
                    })}
                    <button type="button" onClick={() => addDayTopic(idx)} className="text-xs font-semibold text-[#1CA3B0] hover:underline mt-1 inline-flex items-center gap-1" data-testid={`add-topic-${idx}`}>
                      <i className="fas fa-plus text-[10px]"></i> Shto teme
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3 pb-8">
          <button type="submit" disabled={saving || uploading} className="rounded-lg px-8 py-3 font-semibold text-sm text-white inline-flex items-center gap-2 transition-all disabled:opacity-50 hover:-translate-y-0.5" style={{ background: "linear-gradient(130deg, #3CD4E1 0%, #003F72 100%)" }} data-testid="save-event-btn">
            <Save size={16} /> {saving ? "Duke ruajtur..." : isEditing ? "Perditeso Eventin" : "Ruaj Eventin"}
          </button>
          <button type="button" onClick={() => navigate("/paneli/events")} className="rounded-lg px-8 py-3 font-semibold text-sm border border-slate-200 text-[#4A5568] hover:bg-slate-50 transition-colors" data-testid="cancel-event-btn">
            Mbyll
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminEventForm;
